import { useEffect, type RefObject } from "react";

interface UseScrollSentinelArgs {
  /** The scrolling element the sentinel lives inside. */
  rootRef: RefObject<HTMLElement | null>;
  /** Element at the end of the list. */
  targetRef: RefObject<HTMLElement | null>;
  /** False while there is nothing more to load, or a load is in flight. */
  enabled: boolean;
  onReach: () => void;
}

/**
 * Loads the next page when the end of the list comes into view.
 *
 * A "Load more" button asks the user to do the scheduler's job; an observer
 * with a rootMargin fetches while the rows are still off-screen, so the list
 * simply never runs out. `enabled` gates re-entry — without it the observer
 * fires again for every intersection while the request is still in flight.
 */
export const useScrollSentinel = ({
  rootRef,
  targetRef,
  enabled,
  onReach,
}: UseScrollSentinelArgs) => {
  useEffect(() => {
    const root = rootRef.current;
    const target = targetRef.current;
    if (!root || !target || !enabled) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onReach();
      },
      { root, rootMargin: "160px" },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [rootRef, targetRef, enabled, onReach]);
};
